import { base64ToBlob, blobToBase64 } from "@9amhealth/shared";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import { fileExtension, hasFileExtension } from "src/lib/fileExtension";
import { isHybridApp } from "src/lib/platform";
import { ensureOwnership, getDb } from "./DbService";
import type { CachedFile } from "./dbTypes";

const FILE_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const FILE_CACHE_MAX_BYTES = 100 * 1024 * 1024;
const NATIVE_CACHE_DIR = "9amFileCache";

const isExpired = (cachedAt: number): boolean =>
  Date.now() - cachedAt > FILE_CACHE_TTL_MS;

const hasContent = (entry: CachedFile): boolean =>
  Boolean(entry.bytes ?? entry.blob ?? entry.nativeUri);

export const DbFileService = {
  async getFile(fileId: string): Promise<CachedFile | null> {
    await ensureOwnership();
    const db = getDb();
    const entry = await db.cachedFiles.get(fileId);
    if (!entry) {
      return null;
    }
    if (isExpired(entry.cachedAt)) {
      void db.cachedFiles.delete(fileId);
      return null;
    }
    if (!hasContent(entry)) {
      return null;
    }
    // Entries cached before extensions were added to the path hold a URI that
    // other apps cannot open. Drop them so the file is fetched again under a
    // name they can resolve, rather than waiting out the 30 day TTL. Only do
    // this when an extension can actually be resolved for this file — if it
    // can't, re-downloading would produce the same unresolvable path again,
    // so serve the cached entry instead of looping forever.
    if (
      entry.nativeUri &&
      !hasFileExtension(entry.nativeUri) &&
      fileExtension(entry.filename, entry.mimeType)
    ) {
      await this.removeFile(fileId);
      return null;
    }
    return entry;
  },

  async getFileMetadata(
    fileId: string
  ): Promise<Pick<
    CachedFile,
    "contentType" | "contentLength" | "filename" | "fileType"
  > | null> {
    await ensureOwnership();
    const db = getDb();
    const entry = await db.cachedFiles.get(fileId);
    if (!entry) {
      return null;
    }
    if (isExpired(entry.cachedAt)) {
      void db.cachedFiles.delete(fileId);
      return null;
    }
    return {
      contentType: entry.contentType,
      contentLength: entry.contentLength,
      filename: entry.filename,
      fileType: entry.fileType
    };
  },

  async putFile(entry: CachedFile): Promise<void> {
    await ensureOwnership();
    const db = getDb();
    await db.cachedFiles.put(entry);
    void this.evictIfOverLimit();
  },

  async putFileMetadata(
    fileId: string,
    metadata: {
      contentType: string | null;
      contentLength: number | null;
      filename: string | null;
      fileType: string | null;
    }
  ): Promise<void> {
    await ensureOwnership();
    const db = getDb();
    const existing = await db.cachedFiles.get(fileId);
    if (existing) {
      await db.cachedFiles.update(fileId, {
        contentType: metadata.contentType,
        contentLength: metadata.contentLength,
        filename: metadata.filename ?? existing.filename,
        fileType: metadata.fileType
      });
    } else {
      await db.cachedFiles.put({
        id: fileId,
        filename: metadata.filename ?? "",
        mimeType: metadata.contentType ?? "",
        contentType: metadata.contentType,
        contentLength: metadata.contentLength,
        fileType: metadata.fileType,
        cachedAt: Date.now(),
        sizeBytes: 0
      });
    }
  },

  async removeFile(fileId: string): Promise<void> {
    await ensureOwnership();
    const db = getDb();
    const entry = await db.cachedFiles.get(fileId);
    if (entry?.nativeUri) {
      try {
        await Filesystem.deleteFile({ path: entry.nativeUri });
      } catch {
        // best effort
      }
    }
    await db.cachedFiles.delete(fileId);
  },

  async clearAll(): Promise<void> {
    if (isHybridApp()) {
      try {
        await Filesystem.rmdir({
          path: NATIVE_CACHE_DIR,
          directory: Directory.Cache,
          recursive: true
        });
      } catch {
        // best effort
      }
    }
    await ensureOwnership();
    const db = getDb();
    await db.cachedFiles.clear();
  },

  async getCacheStats(): Promise<{ count: number; totalSize: number }> {
    await ensureOwnership();
    const db = getDb();
    const all = await db.cachedFiles.toArray();
    const totalSize = all.reduce((sum, f) => sum + f.sizeBytes, 0);
    return { count: all.length, totalSize };
  },

  async evictIfOverLimit(): Promise<void> {
    try {
      await ensureOwnership();
      const db = getDb();
      const all = await db.cachedFiles.orderBy("cachedAt").toArray();
      let totalSize = all.reduce((sum, f) => sum + f.sizeBytes, 0);
      if (totalSize <= FILE_CACHE_MAX_BYTES) {
        return;
      }

      for (const entry of all) {
        if (totalSize <= FILE_CACHE_MAX_BYTES) {
          break;
        }
        if (entry.nativeUri) {
          try {
            await Filesystem.deleteFile({ path: entry.nativeUri });
          } catch {
            // best effort
          }
        }
        await db.cachedFiles.delete(entry.id);
        totalSize -= entry.sizeBytes;
      }
    } catch {
      // eviction is best-effort
    }
  },

  async evictExpired(): Promise<void> {
    try {
      await ensureOwnership();
      const db = getDb();
      const cutoff = Date.now() - FILE_CACHE_TTL_MS;
      const expired = await db.cachedFiles
        .where("cachedAt")
        .below(cutoff)
        .toArray();
      for (const entry of expired) {
        if (entry.nativeUri) {
          try {
            await Filesystem.deleteFile({ path: entry.nativeUri });
          } catch {
            // best effort
          }
        }
      }
      await db.cachedFiles.where("cachedAt").below(cutoff).delete();
    } catch {
      // eviction is best-effort
    }
  }
};

export async function readCachedBlob(entry: CachedFile): Promise<Blob | null> {
  if (entry.bytes) {
    return new Blob([entry.bytes], { type: entry.mimeType });
  }
  if (entry.blob) {
    return entry.blob;
  }
  if (entry.nativeUri) {
    try {
      const result = await Filesystem.readFile({ path: entry.nativeUri });
      const base64Data = result.data as string;
      const mimeType = entry.mimeType || "application/octet-stream";
      return base64ToBlob(base64Data, mimeType);
    } catch {
      return null;
    }
  }
  return null;
}

const nativeDirCreated = new Set<string>();

async function ensureNativeCacheDir(): Promise<void> {
  if (nativeDirCreated.has(NATIVE_CACHE_DIR)) {
    return;
  }
  try {
    await Filesystem.mkdir({
      path: NATIVE_CACHE_DIR,
      directory: Directory.Cache,
      recursive: true
    });
  } catch {
    // already exists
  }
  nativeDirCreated.add(NATIVE_CACHE_DIR);
}

export async function cacheDownloadedFile(
  fileId: string,
  blob: Blob,
  filename: string,
  mimeType: string,
  metadata?: {
    contentType: string | null;
    contentLength: number | null;
    fileType: string | null;
  }
): Promise<CachedFile> {
  const entry: CachedFile = {
    id: fileId,
    filename,
    mimeType: mimeType || blob.type || "application/octet-stream",
    contentType: metadata?.contentType ?? null,
    contentLength: metadata?.contentLength ?? null,
    fileType: metadata?.fileType ?? null,
    cachedAt: Date.now(),
    sizeBytes: blob.size
  };

  if (isHybridApp()) {
    await ensureNativeCacheDir();
    const base64Data = await blobToBase64(blob);
    const extension = fileExtension(filename, entry.mimeType);
    const suffix = extension ? `.${extension}` : "";
    const cachePath = `${NATIVE_CACHE_DIR}/${fileId}${suffix}`;
    await Filesystem.writeFile({
      path: cachePath,
      data: base64Data,
      directory: Directory.Cache
    });
    const uriResult = await Filesystem.getUri({
      path: cachePath,
      directory: Directory.Cache
    });
    entry.nativeUri = uriResult.uri;
  } else {
    // WebKit rejects Blob values in IndexedDB in some contexts (e.g. WKWebView).
    entry.bytes = await blob.arrayBuffer();
  }

  await DbFileService.putFile(entry);
  return entry;
}

export async function tryCacheDownloadedFile(
  ...args: Parameters<typeof cacheDownloadedFile>
): Promise<CachedFile | null> {
  try {
    return await cacheDownloadedFile(...args);
  } catch (error) {
    addSentryBreadcrumb(
      "fileCache",
      "Caching downloaded file failed",
      "warning",
      { fileId: args[0], error: String(error) }
    );
    return null;
  }
}
